import { useCallback, useEffect, useMemo, useState } from 'react'
import { Spin, Tooltip } from 'antd'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { operationLogsApi } from '@/services/operationLogs/api'
import { municipalitiesApi } from '@/services/municipalities/api'
import type { OperationLogResponse } from '@/services/operationLogs/types'
import { isHandledAuthError } from '@/services/client'
import { useBackToParent, usePersistentPageSize } from '@/hooks'
import { usePermission } from '@/hooks/usePermission'
import useSearchPersistence from '@/hooks/useSearchPersistence'
import { useColumnVisibility } from '@/hooks/useColumnVisibility'
import { Pagination, BackButton, SearchCard, ResultCard, ResizeHandle, SortableHeader, TableLoadingRow, DemoInput, DemoSelect, DownloadIcon } from '@/widgets/demo/ListPrimitives';
import { ColumnVisibilityControl } from '@/widgets/demo/ColumnVisibilityControl';
import { demoButtonClass, computePagination, useResizableColumns, useSortState, type ColumnWidthMap, type SortOrder } from '@/widgets/demo/listHelpers';
const LOG_OPERATION_COLUMN_DEFAULTS: ColumnWidthMap = {
  loggedAt: 176,
  userDisplayId: 120,
  userName: 128,
  municipality: 144,
  action: 128,
  summary: 232,
  detail: 216,
  ipAddress: 128,
}

const LOG_OPERATION_COLUMN_ORDER_BASE = [
  'loggedAt',
  'userDisplayId',
  'userName',
  'action',
  'summary',
  'detail',
  'ipAddress',
] as const

const LOG_OPERATION_COLUMN_ORDER_SYSADMIN = [
  'loggedAt',
  'userDisplayId',
  'userName',
  'municipality',
  'action',
  'summary',
  'detail',
  'ipAddress',
] as const

// 列表示設定（No.30 横展開）用の画面キーと、列ID→日本語ラベルのマップ。
// ラベルは一覧ヘッダの表示テキストと揃える。
const LOG_OPERATION_COLUMN_VISIBILITY_KEY = 'log-operation-history'

// 日時(loggedAt)は監査ログの主たる識別軸（行の同定/並びの基準）のため常に表示にする。
// 選択列・操作列はこの画面に無い。それ以外は切替可。
const LOG_OPERATION_ALWAYS_VISIBLE_COLUMNS = ['loggedAt']

const LOG_OPERATION_COLUMN_LABELS: Record<string, string> = {
  loggedAt: '日時',
  userDisplayId: 'ユーザID',
  userName: 'ユーザ名',
  municipality: '自治体',
  action: '操作種別',
  summary: '概要',
  detail: '詳細',
  ipAddress: 'IPアドレス',
}

const ACTION_OPTIONS: { label: string; value: number }[] = [
  { label: 'DB更新', value: 1 },
  { label: '検索', value: 2 },
  { label: 'ログイン', value: 3 },
  { label: 'ログアウト', value: 4 },
  { label: 'ファイル出力', value: 5 },
]

const LOGGED_AT_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/

function formatLoggedAt(value: string | null): string {
  if (!value) return ''

  const matched = value.match(LOGGED_AT_PATTERN)
  if (!matched) return value

  const [, year, month, day, hour, minute, second] = matched
  return `${year}/${month}/${day} ${hour}:${minute}:${second}`
}

function toDateInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function defaultDateRange(): { dateFrom: string; dateTo: string } {
  const today = new Date()
  const from = new Date(today)
  from.setMonth(from.getMonth() - 1)
  return { dateFrom: toDateInputValue(from), dateTo: toDateInputValue(today) }
}

interface LogSearchState {
  userName: string
  action: string
  municipalityId: string
  dateFrom: string
  dateTo: string
  keyword: string
  sortBy: string | null
  sortOrder: SortOrder
  page: number
}

const LogOperationHistory = () => {
  const goBackToParent = useBackToParent()
  const { isSystemAdmin } = usePermission()
  const initialDateRange = defaultDateRange()
  // 検索条件は最後に適用した結果を保持する（顧客要望）。適用済みフィルタ・並び順・ページ番号を
  // localStorage（search_<sub>_log-operation-history）へ保存し、再訪問時に復元する。
  const [savedSearch, setSavedSearch] = useSearchPersistence<LogSearchState>('log-operation-history', {
    userName: '',
    action: '',
    municipalityId: '',
    dateFrom: initialDateRange.dateFrom,
    dateTo: initialDateRange.dateTo,
    keyword: '',
    sortBy: null,
    sortOrder: null,
    page: 1,
  })
  const [page, setPage] = useState(savedSearch.page ?? 1)
  const [pageSize, setPageSize] = usePersistentPageSize('log-operation-history')
  const [totalElements, setTotalElements] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [rows, setRows] = useState<OperationLogResponse[]>([])
  const [loading, setLoading] = useState(false)
  const { sort, setSort, sortQuery } = useSortState({ sortBy: savedSearch.sortBy, order: savedSearch.sortOrder })
  const { widths: colWidths, startResize } = useResizableColumns({ defaults: LOG_OPERATION_COLUMN_DEFAULTS })
  // ロールで実際に描画する列順（BASE/SYSADMIN）を解決してから列表示設定フックへ渡す。
  const columnOrder = useMemo<readonly string[]>(
    () => (isSystemAdmin ? LOG_OPERATION_COLUMN_ORDER_SYSADMIN : LOG_OPERATION_COLUMN_ORDER_BASE),
    [isSystemAdmin],
  )
  // 列表示設定（No.30 横展開）。ロール解決後の columnOrder を allColumnIds に渡すので、
  // 設定UIのチェック項目もそのロールに見えている列だけになる。hidden 集合方式のため、
  // 別ロールで列数が変わっても未知列は既定表示で安全（壊れない）。
  const { isVisible: isColVisible, setVisible: setColVisible, reset: resetCols, hiddenCount: hiddenColCount } =
    useColumnVisibility(LOG_OPERATION_COLUMN_VISIBILITY_KEY, columnOrder, {
      alwaysVisible: LOG_OPERATION_ALWAYS_VISIBLE_COLUMNS,
    })
  const visibleColumnOrder = columnOrder.filter((id) => isColVisible(id))
  const columnVisibilityItems = useMemo(
    () => columnOrder.map((id) => ({ id, label: LOG_OPERATION_COLUMN_LABELS[id] ?? id })),
    [columnOrder],
  )
  // 隠した列は colWidths から除外して合計幅を出す（隙間/幅ズレ防止）。
  const tableWidth = visibleColumnOrder.reduce((sum, id) => sum + (colWidths[id] ?? 0), 0)
  // tbody の空行/ローディング行 colSpan は「表示中の列数 + 末尾の余白 col(1)」。
  const visibleColSpan = visibleColumnOrder.length + 1
  const handleSortChange = (next: typeof sort) => {
    setSort(next)
    setPage(1)
  }

  const [municipalityOptions, setMunicipalityOptions] = useState<{ id: number; name: string }[]>([])
  const [qUserName, setQUserName] = useState(savedSearch.userName)
  const [qAction, setQAction] = useState(savedSearch.action)
  const [qMunicipalityId, setQMunicipalityId] = useState(savedSearch.municipalityId)
  const [qDateFrom, setQDateFrom] = useState(savedSearch.dateFrom)
  const [qDateTo, setQDateTo] = useState(savedSearch.dateTo)
  const [qKeyword, setQKeyword] = useState(savedSearch.keyword)

  const [applied, setApplied] = useState({
    userName: savedSearch.userName,
    action: savedSearch.action,
    municipalityId: savedSearch.municipalityId,
    dateFrom: savedSearch.dateFrom,
    dateTo: savedSearch.dateTo,
    keyword: savedSearch.keyword,
  })
  const [downloadLoading, setDownloadLoading] = useState<string | null>(null)

  useEffect(() => {
    if (!isSystemAdmin) return
    let cancelled = false
    void (async () => {
      try {
        const { data } = await municipalitiesApi.list()
        if (cancelled) return
        setMunicipalityOptions(data.map((m) => ({ id: m.id, name: m.name })))
      } catch {
        // ignore
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isSystemAdmin])

  const fetchData = useCallback(async (p: number) => {
    setLoading(true)
    try {
      const params: NonNullable<Parameters<typeof operationLogsApi.list>[0]> = {
        page: p - 1,
        size: pageSize,
        ...sortQuery,
      }
      if (applied.userName.trim()) params.userName = applied.userName.trim()
      if (applied.action) params.action = Number(applied.action)
      if (applied.municipalityId) params.municipalityId = Number(applied.municipalityId)
      if (applied.keyword.trim()) params.keyword = applied.keyword.trim()
      if (applied.dateFrom) params.dateFrom = applied.dateFrom
      if (applied.dateTo) params.dateTo = applied.dateTo

      const { data } = await operationLogsApi.list(params)
      // 取得ページが範囲外（永続化された page が絞り込み後の総ページ数を超える等）で
      // 0件返却された場合、該当データはあるのに「データなし」表示になる。最終ページへ寄せて再取得する。
      if (data.content.length === 0 && data.totalElements > 0 && p > 1) {
        const lastPage = Math.max(1, Math.ceil(data.totalElements / pageSize))
        setTotalElements(data.totalElements)
        setPage(lastPage)
        return
      }
      setRows(data.content)
      setTotalElements(data.totalElements)
      setTotalPages(data.totalPages || 1)
    } catch (error) {
      setRows([])
      setTotalElements(0)
      setTotalPages(1)
      if (isHandledAuthError(error)) return
    } finally {
      setLoading(false)
    }
  }, [applied, pageSize, sortQuery])

  useEffect(() => {
    // 検索条件・ページ変更に応じて一覧を取得する正当なデータ取得 effect。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchData(page)
  }, [page, fetchData])

  // 適用済みフィルタ・並び順・ページ番号を保存し、再訪問時に前回の検索結果を復元する。
  useEffect(() => {
    setSavedSearch({ ...applied, sortBy: sort.sortBy, sortOrder: sort.order, page })
  }, [applied, sort.sortBy, sort.order, page, setSavedSearch])

  const handleSearch = () => {
    setLoading(true)
    setPage(1)
    setApplied({
      userName: qUserName,
      action: qAction,
      municipalityId: qMunicipalityId,
      dateFrom: qDateFrom,
      dateTo: qDateTo,
      keyword: qKeyword,
    })
  }

  const handleClear = () => {
    setLoading(true)
    const dates = defaultDateRange()
    setQUserName('')
    setQAction('')
    setQMunicipalityId('')
    setQDateFrom(dates.dateFrom)
    setQDateTo(dates.dateTo)
    setQKeyword('')
    setPage(1)
    setApplied({
      userName: '',
      action: '',
      municipalityId: '',
      dateFrom: dates.dateFrom,
      dateTo: dates.dateTo,
      keyword: '',
    })
  }

  const handleCsvDownload = async () => {
    setDownloadLoading('操作ログをダウンロードしています...')
    try {
      const params: NonNullable<Parameters<typeof operationLogsApi.exportCsv>[0]> = {}
      if (applied.userName.trim()) params.userName = applied.userName.trim()
      if (applied.action) params.action = Number(applied.action)
      if (applied.municipalityId) params.municipalityId = Number(applied.municipalityId)
      if (applied.keyword.trim()) params.keyword = applied.keyword.trim()
      if (applied.dateFrom) params.dateFrom = applied.dateFrom
      if (applied.dateTo) params.dateTo = applied.dateTo

      const resp = await operationLogsApi.exportCsv(params)
      const blob = new Blob([resp.data], { type: 'text/csv; charset=UTF-8' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '')
      anchor.href = url
      anchor.download = `操作ログ_${stamp}.csv`
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      if (isHandledAuthError(error)) return
    } finally {
      setDownloadLoading(null)
    }
  }

  const { safePage, rangeFrom, rangeTo } = computePagination(page, totalElements, pageSize)

  return (
    <div className="min-h-screen bg-gray-100">
      {downloadLoading && <Spin fullscreen tip={downloadLoading} />}
      <SystemHeader breadcrumbs={[{ label: 'システム管理', page: 'submenu-admin' }, { label: 'ログ操作履歴' }]} />
      <div className="mx-auto w-full max-w-[1600px] px-6 py-8">
        <BackButton onClick={goBackToParent} />

        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          全ユーザの操作ログを参照できます。DB更新・検索・ログイン・ログアウト・ファイル出力などを記録します。
          <strong className="font-semibold text-amber-900"> 削除はできません</strong>（監査用）。
          <span className="text-amber-800"> 現在の検索条件に一致する一覧を CSV でダウンロードできます。</span>
        </div>

        <SearchCard className="mb-6">
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">ユーザ（ID・氏名の一部）</label>
              <DemoInput type="text" value={qUserName} onChange={(e) => setQUserName(e.target.value)} placeholder="例: admin / 田中" className="w-full" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">操作種別</label>
              <DemoSelect value={qAction} onChange={(e) => setQAction(e.target.value)} className="w-full">
                <option value="">全て</option>
                {ACTION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </DemoSelect>
            </div>
            {isSystemAdmin && (
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">自治体</label>
                <DemoSelect value={qMunicipalityId} onChange={(e) => setQMunicipalityId(e.target.value)} className="w-full">
                  <option value="">全て</option>
                  {municipalityOptions.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </DemoSelect>
              </div>
            )}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">キーワード（概要・詳細）</label>
              <DemoInput type="text" value={qKeyword} onChange={(e) => setQKeyword(e.target.value)} placeholder="部分一致" className="w-full" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">期間（開始）</label>
              <DemoInput type="date" value={qDateFrom} onChange={(e) => setQDateFrom(e.target.value)} className="w-full" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">期間（終了）</label>
              <DemoInput type="date" value={qDateTo} onChange={(e) => setQDateTo(e.target.value)} className="w-full" />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={handleClear} className={demoButtonClass.secondary}>クリア</button>
            <button type="button" onClick={handleSearch} className={demoButtonClass.primary}>検索</button>
          </div>
        </SearchCard>

        <ResultCard
          title="操作ログ一覧"
          extra={
            <>
              <span className="text-xs text-gray-500">参照のみ（改ざん不可）</span>
              <ColumnVisibilityControl
                columns={columnVisibilityItems}
                isVisible={isColVisible}
                setVisible={setColVisible}
                onReset={resetCols}
                alwaysVisible={LOG_OPERATION_ALWAYS_VISIBLE_COLUMNS}
                hiddenCount={hiddenColCount}
              />
              <button type="button" onClick={handleCsvDownload} disabled={totalElements === 0} className={totalElements === 0 ? demoButtonClass.disabled : demoButtonClass.slate}>
                <DownloadIcon />
                CSVダウンロード
              </button>
            </>
          }
        >
          <div className="overflow-x-auto">
            <table className="min-w-full table-fixed" style={{ width: `${tableWidth}px` }}>
              <colgroup>
                {visibleColumnOrder.map((id) => (
                  <col key={id} style={{ width: `${colWidths[id]}px` }} />
                ))}
                <col />
              </colgroup>
              <thead className="border-b-2 border-gray-200 bg-gray-50">
                <tr>
                  {isColVisible('loggedAt') && (
                    <SortableHeader field="loggedAt" label="日時" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="loggedAt" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('userDisplayId') && (
                    <SortableHeader field="userDisplayId" label="ユーザID" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="userDisplayId" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('userName') && (
                    <SortableHeader field="userName" label="ユーザ名" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="userName" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('municipality') && (
                    <th className="relative px-2 py-3 text-left text-sm font-medium text-gray-700 whitespace-nowrap">
                      自治体
                      <ResizeHandle columnId="municipality" onMouseDown={startResize} />
                    </th>
                  )}
                  {isColVisible('action') && (
                    <SortableHeader field="action" label="操作種別" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="action" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('summary') && (
                    <SortableHeader field="summary" label="概要" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="summary" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('detail') && (
                    <th className="relative px-2 py-3 text-left text-sm font-medium text-gray-700">
                      詳細
                      <ResizeHandle columnId="detail" onMouseDown={startResize} />
                    </th>
                  )}
                  {isColVisible('ipAddress') && (
                    <SortableHeader field="ipAddress" label="IPアドレス" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" />
                  )}
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody className={`divide-y divide-gray-200 transition-opacity ${loading ? 'opacity-60' : ''}`}>
                {rows.length === 0 ? (
                  loading ? (
                    <TableLoadingRow colSpan={visibleColSpan} />
                  ) : (
                    <tr>
                      <td colSpan={visibleColSpan} className="px-2 py-8 text-center text-sm text-gray-500">
                        該当するデータがありません
                      </td>
                    </tr>
                  )
                ) : (
                  rows.map((r) => (
                    <tr key={r.id} className="transition hover:bg-blue-50">
                      {isColVisible('loggedAt') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={formatLoggedAt(r.loggedAt)} placement="topLeft">
                            <span className="block truncate">{formatLoggedAt(r.loggedAt)}</span>
                          </Tooltip>
                        </td>
                      )}
                      {isColVisible('userDisplayId') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={r.userDisplayId || r.userId || '-'} placement="topLeft">
                            <span className="block truncate">{r.userDisplayId || r.userId || '-'}</span>
                          </Tooltip>
                        </td>
                      )}
                      {isColVisible('userName') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={r.userName} placement="topLeft">
                            <span className="block truncate">{r.userName}</span>
                          </Tooltip>
                        </td>
                      )}
                      {isColVisible('municipality') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={r.municipalityName || '－'} placement="topLeft">
                            <span className="block truncate">{r.municipalityName || '－'}</span>
                          </Tooltip>
                        </td>
                      )}
                      {isColVisible('action') && (
                        <td className="px-2 py-3 truncate">
                          <span className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-800">{r.operationType}</span>
                        </td>
                      )}
                      {isColVisible('summary') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={r.summary} placement="topLeft">
                            <span className="block truncate">{r.summary}</span>
                          </Tooltip>
                        </td>
                      )}
                      {isColVisible('detail') && (
                        <td className="px-2 py-3 text-sm text-gray-500">
                          <Tooltip title={r.detail} placement="topLeft">
                            <span className="block truncate">{r.detail}</span>
                          </Tooltip>
                        </td>
                      )}
                      {isColVisible('ipAddress') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={r.ipAddress} placement="topLeft">
                            <span className="block truncate">{r.ipAddress}</span>
                          </Tooltip>
                        </td>
                      )}
                      <td aria-hidden="true" />
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination currentPage={safePage} totalPages={totalPages} total={totalElements} rangeFrom={rangeFrom} rangeTo={rangeTo} onPageChange={(next) => { setLoading(true); setPage(next); }} pageSize={pageSize} onPageSizeChange={(next) => { setLoading(true); setPageSize(next); setPage(1); }} />
        </ResultCard>
      </div>
    </div>
  )
}

export default LogOperationHistory
